<script lang="ts">
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { onDestroy, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import Page from '../components/misc/Page.svelte';
  import Button from '../components/form/Button.svelte';
  import TextInputCurrency from '../components/form/TextInputCurrency.svelte';
  import {
    formatMoney,
    getErrorMessage,
    pluralizeI18n,
    isMobile,
    isAndroid
  } from '../etc/utils';
  import { ToastQueue, AppState } from '../etc/stores';
  import { Backend } from '../services/backend';
  import { Customer } from '../services/customer';
  import { Container } from '../services/container';
  import { Logger } from '../services/logger';
  import Analytics from '../services/analytics';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import { Wix } from '../services/wix';
  import { Auth } from '../services/auth';
  import Heading from '../components/typography/Heading.svelte';
  import Text from '../components/typography/Text.svelte';
  import Label from '../components/typography/Label.svelte';
  import { useFinishSignIn } from '../etc/hooks/useModalInternal';
  import { replaceVariables } from '../i18n';
  import CoinIcon from '../components/icons/CoinV2.svelte';
  import RewardCoin from '../components/misc/RewardCoin.svelte';
  import CopyCode from '../components/misc/CopyCode.svelte';
  import { fade } from 'svelte/transition';
  import CoinsExpiringSoonDisclaimer from '../components/coins-expiring-soon-disclaimer/CoinsExpiringSoonDisclaimer.svelte';
  import flagKeys from '../etc/flagKeys';
  import ExpiringCountdown from '../components/expiring-countdown/ExpiringCountdown.svelte';

  const location = useLocation();
  const navigate = useNavigate();
  const auth = Container.get(Auth);
  const backend = Container.get(Backend);
  const customer = Container.get(Customer);
  const logger = Container.get(Logger).context('ConvertCoins.svelte') as Logger;

  let couponData = null;

  let loading = false;
  let startAnimation = false;

  let step = 0;

  let inputAmount = '';
  let amount = null;
  let isInputFocused;
  let paymentCodeIsExpired = false;

  let coinsWallet = Number($AppState.customer.wallet.coins);

  let handleNavigationBackPage;
  let timeoutAnimation;

  const navAnimation: boolean = $location.state?.navAnimation;

  const { open, close } = useFinishSignIn();

  $: mobileKeyboard = isInputFocused && isMobile();
  $: expirableCoinsIsActive = $AppState.featureFlags.includes(flagKeys.EXPIRABLE_COINS);

  let defaultData = {
    moneyValue: amount || 0,
    code: 'XXXXXXXXX',
    status: 'active'
  };

  $: price = formatMoney($AppState.business.location, amount);

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`convert_coins.${text}`), values);
  };

  const setFocus = event => {
    isInputFocused = event.detail;
    if (!event.detail && !isAndroid())
      setTimeout(() => {
        window.scrollTo({ top: 0 });
      }, 500);
  };

  const onInputChange = event => {
    let value = event.detail.toString().replace(/[^0-9\.]+/g, '');
    if (isNaN(value)) value = 0;
    inputAmount = value;
    amount = value;
    defaultData.moneyValue = value;
  };

  const convertFirstStep = () => {
    if (amount <= 0) {
      ToastQueue.add({
        title: $_('error.title'),
        message: t('errors.higher_then'),
        type: 'error',
        duration: 5000
      });

      return;
    }

    if (coinsWallet < amount) {
      ToastQueue.add({
        title: t('errors.not_enough_coins.title'),
        message: t('errors.not_enough_coins.message'),
        type: 'error',
        duration: 7000
      });

      return;
    }

    nextStep();
  };

  const nextStep = () => {
    step += 1;
  };

  const previousStep = () => {
    step -= 1;
  };

  const onBack = () => {
    close();
    if (step === 1) {
      previousStep();
      onFocusInput();
    } else {
      if (navAnimation) {
        clearTimeout(timeoutAnimation);
        startAnimation = true;
        timeoutAnimation = setTimeout(() => {
          if (handleNavigationBackPage) handleNavigationBackPage();
          navigate(-1);
        }, 600);
      } else {
        navigate(-1);
      }
    }
  };

  const navigatePaymentCodes = () => {
    navigate('/payment-codes');
  };

  const onCopy = () => {
    Analytics.logEvent('click-copy-code', 'convert-coins-state');
  };

  const convertCoins = async () => {
    const amountInCents = amount * 100;

    loading = true;

    try {
      const response = await backend.createCoupon({
        oauthId: $AppState.business.oauthId,
        value: Number(amountInCents.toFixed(2))
      });

      couponData = response;

      await customer.loadState();

      Analytics.logEvent('click-convert-coins-confirm', 'convert-coins-state');

      nextStep();
    } catch (e) {
      logger.error(e);
      const message = getErrorMessage(e);

      ToastQueue.add({
        title: $_('error.title'),
        message,
        type: 'error',
        duration: 5000
      });

      loading = false;
    }
  };

  const onFocusInput = () => {
    if (isMobile()) {
      if (auth.provider === 'wix') {
        Wix.togglePageMenu();
      }
    }
    if (isAndroid() || !isMobile())
      setTimeout(() => {
        document.getElementById('twism-amount').focus();
      }, 400);
  };

  const handleExpired = () => {
    paymentCodeIsExpired = true;
  };

  onMount(async () => {
    Analytics.logPageView('Convert Coins', '/convert-coins');
    if (await auth.isSoftLogin()) {
      open();
    } else {
      onFocusInput();
    }
  });

  onDestroy(() => {
    clearTimeout(timeoutAnimation);
  });
</script>

<Page padding="0" hideCloseButton>
  <HeaderInternal
    title={t('title')}
    {onBack}
    {navAnimation}
    {startAnimation}
    bind:handleNavigationBackPage
  />
  <div
    class="page-container"
    class:--animation={startAnimation}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    {#if step === 0}
      <div class="step">
        <div class="step__center" class:--mobile-keyboard={mobileKeyboard}>
          <div class="twism-title">
            <Heading type="h2" textAlign="center">
              {t('title_input')}
            </Heading>
          </div>
          <div class="available_coins">
            <Heading
              class="available_coins__title"
              type="h3"
              textAlign="center"
            >
              {t(pluralizeI18n('available', coinsWallet), {
                COIN: coinsWallet
              })}
            </Heading>
          </div>
          <div class="convert-input">
            <TextInputCurrency
              autoFocus={false}
              name="twism-amount"
              bind:value={inputAmount}
              on:input={onInputChange}
              on:focus={setFocus}
              maxLength={6}
              disableFocusScrollIOS={true}
            >
              <CoinIcon
                let:isFocused
                slot="icon"
                color={!isFocused && '#bcc0c4'}
              />
            </TextInputCurrency>
          </div>
          <Text component="p" textAlign="center">
            {t('value', {
              CURRENCY: $AppState.business.location.currency.replace(
                'USD',
                'US$'
              ),
              VALUE: price
            })}
          </Text>
        </div>
        <div class="step-actions convert-btn">
          <div class="step-actions-container">
            <CoinsExpiringSoonDisclaimer />
            <Button
              label={t('next')}
              on:click={() => convertFirstStep()}
              disabled={!amount}
            />
          </div>
        </div>
      </div>
    {:else if step === 1}
      <div class="step step-2">
        <div class="step__center">
          <div class="coin-wrapper">
            <RewardCoin size="128" value={defaultData.moneyValue} />
          </div>
          <Heading type="h3" textAlign="center" class="twism-title-coin">
            {@html t(pluralizeI18n('ready_convert', amount), {
              AMOUNT: amount,
              AMOUNT_CURRENCY: price
            })}
          </Heading>
        </div>
        <div class="step-actions">
          <Button
            label={t('btn_confirm')}
            on:click={() => convertCoins()}
            {loading}
          />
        </div>
      </div>
    {:else if step === 2}
      <div class="step">
        <div class={`twm-convert-coins-alert twm-convert-coins-alert--${paymentCodeIsExpired ? 'expired' : 'success'}`}>
          <Label
            type="medium-regular-semibold"
            textAlign="center">{paymentCodeIsExpired ? t('expired-message'): t('created-success')}</Label
          >
        </div>
        <div class="step__center">
          <div class="coin-wrapper">
            {#if couponData}
              <RewardCoin size="128" value={couponData.moneyValue / 100} />
            {/if}
          </div>
          <Text
            type="default"
            textAlign="center"
            class="twism-convert-description"
          >
            {@html `${paymentCodeIsExpired ? t('description-expired') : t('description')}`}
          </Text>
        </div>
        <div class="step-actions">
          <CopyCode disabled={paymentCodeIsExpired} code={couponData.code} on:copy={onCopy}>
            <div slot="bottom-component">
              {#if expirableCoinsIsActive && couponData?.expiresAt}
              <ExpiringCountdown
                emitExpiredEventOnFirstTime
                showExpiredLabel
                expiresAt={couponData.expiresAt}
                on:expired={handleExpired}
              />
              {/if}
            </div>
          </CopyCode>

          <Text
            type="small"
            class="button-link"
            textAlign="center"
            on:click={() => navigatePaymentCodes()}
          >
            {t('see_my_codes')}
          </Text>
        </div>
      </div>
    {/if}
  </div>
</Page>

<style lang="scss">
  :global(.twism-title-coin) {
    line-height: 150% !important;
    max-width: 325px;
    align-self: center;
    font-weight: 400 !important;
    margin-top: 32px !important;
    margin-bottom: 4px;
  }

  :global(.available_coins__title) {
    font-weight: 500 !important;
    line-height: 27px !important;
  }

  .page-container {
    width: 100%;
    height: 100%;

    margin-bottom: 10px;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    transition: opacity 0.3s ease-in-out;

    &.--animation {
      opacity: 0;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .steps-wrapper {
    width: 100%;
    display: flex;
  }

  :global(.button-link) {
    line-height: 22px !important;
    margin-top: 24px !important;
    color: var(--twism-theme-text) !important;
    text-decoration-line: underline !important;
    display: block;
    cursor: pointer;
  }

  .step {
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column;

    :global(.twm-expiring-countdown) {
      font-weight: 500;
    }

    &__center {
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: center;
      transition: flex 500ms, padding-top 200ms;

      &.--mobile-keyboard {
        padding-top: 24px;
        flex: 0;
        :global(.twism-title) {
          margin-bottom: 24px;
        }
      }
    }
  }

  .first-coin-wrapper {
    margin: 24px;
  }

  .coin-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .twism-title {
    margin-top: 0;
    margin-bottom: 76px;
    align-self: center;
    max-width: 257px;
    width: 100%;
    transition: margin-bottom 500ms;
    :global(h3) {
      line-height: 28px !important;
    }
  }

  .twm-convert-coins-alert {
    height: 52px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    flex-shrink: 0;

    &--success {
      background: linear-gradient(0deg, #def8f3, #def8f3), #0267c1;

      :global(> p) {
        color: #00A488 !important;
      }
    }

    &--expired {
      background: #FFEAED;

      :global(> p) {
        color: #B52831 !important;
      }
    }
  }

  :global(.twism-convert-description) {
    margin-bottom: 0 !important;
    max-width: 312px;
    align-self: center;
    margin-top: 32px !important;
  }

  .step-actions {
    margin: 0 24px;
    margin-top: 44px;
    margin-bottom: 32px;
  }

  .step-actions-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .convert-input {
    margin: 16px 24px 8px;
  }

  .convert-btn {
    margin-top: 10px;
  }
</style>
